import type { Milestone } from '@just-us/shared'
/**
 * pages/milestone-timeline.tsx
 *
 * Chronological list of couple milestones.
 * - Sticky header with "Add" → /milestones/new
 * - Click card → MilestoneDetailSheet (full title, note, photos, lightbox)
 * - Delete button → ConfirmSheet
 * - Empty state, skeleton loaders
 * - Bottom nav
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import ConfirmSheet from '../components/confirm-sheet'
import MilestoneDetailSheet from '../components/milestone-detail-sheet'
import { useDeleteMilestone, useMilestones } from '../hooks/use-milestones'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateBadge = (dateStr: string): { day: string; month: string } => {
  const d = new Date(`${dateStr}T00:00:00`)
  return {
    day: d.getDate().toString(),
    month: d.toLocaleDateString('en-US', { month: 'short' }),
  }
}

const formatFullDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const MilestoneCardSkeleton = () => (
  <div className="w-full bg-white rounded-[16px] border border-[#F9E0E8] p-4 animate-shimmer flex items-start gap-3">
    <div className="w-[48px] h-[56px] rounded-[12px] bg-[#FBEAF0] shrink-0" />
    <div className="flex-1 flex flex-col gap-2 pt-1">
      <div className="h-[14px] rounded-full bg-[#F4C0D1] w-2/3" />
      <div className="h-[12px] rounded-full bg-[#F9E0E8] w-1/2" />
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Milestone Card
// ---------------------------------------------------------------------------

interface MilestoneCardProps {
  milestone: Milestone
  onClick: (milestone: Milestone) => void
}

const MilestoneCard = ({ milestone, onClick }: MilestoneCardProps) => {
  const badge = formatDateBadge(milestone.date)
  const photoUrls = milestone.photo_urls ?? []
  const hasPhotos = photoUrls.length > 0

  return (
    <button
      type="button"
      onClick={() => onClick(milestone)}
      className="
        w-full bg-white rounded-[16px] border border-[#F4C0D1]
        shadow-[0_2px_12px_rgba(212,83,126,0.08)]
        p-4 flex items-start gap-3 animate-fade-in-up
        text-left active:bg-[#FBEAF0] transition-colors duration-100
        touch-manipulation
      "
    >
      {/* Date badge */}
      <div className="shrink-0 w-[48px] flex flex-col items-center justify-center bg-[#FBEAF0] rounded-[12px] border border-[#F4C0D1] py-[6px]">
        <span className="font-['Playfair_Display'] text-[20px] font-semibold text-[#D4537E] leading-none">
          {badge.day}
        </span>
        <span className="font-['DM_Sans'] text-[10px] font-medium text-[#B89AA2] uppercase tracking-wide mt-[2px]">
          {badge.month}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810] truncate leading-tight">
          {milestone.title}
        </p>
        <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] mt-[2px]">
          {formatFullDate(milestone.date)}
        </p>
        {milestone.note && (
          <p className="font-['DM_Sans'] text-[13px] text-[#7A5A62] mt-[4px] line-clamp-1">
            {milestone.note}
          </p>
        )}
      </div>

      {/* Photo indicator */}
      {hasPhotos && (
        <div className="shrink-0 w-[72px] h-[72px] rounded-[12px] overflow-hidden border border-[#F4C0D1]">
          <img src={photoUrls[0]} alt="Moment" className="w-full h-full object-cover" />
        </div>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MilestoneTimelinePage() {
  const navigate = useNavigate()
  const { data, isLoading } = useMilestones()
  const deleteMilestone = useDeleteMilestone()

  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Milestone | null>(null)

  const milestones = data?.data ?? []

  const handleCardClick = (milestone: Milestone) => {
    setSelectedMilestone(milestone)
  }

  const handleDeleteClick = (milestone: Milestone) => {
    setSelectedMilestone(null)
    setDeleteTarget(milestone)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget) return
    deleteMilestone.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => setDeleteTarget(null),
    })
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Milestones
          </span>
          <button
            type="button"
            onClick={() => navigate('/milestones/new')}
            className="font-['DM_Sans'] text-[14px] font-medium text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
          >
            Add
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[88px] flex flex-col gap-3">
        {isLoading ? (
          <>
            <MilestoneCardSkeleton />
            <MilestoneCardSkeleton />
            <MilestoneCardSkeleton />
          </>
        ) : milestones.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-20 gap-3 animate-fade-in">
            <div className="text-[52px] animate-float">📍</div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              No milestones yet
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
              Mark the moments that matter — big and small.
            </p>
            <button
              type="button"
              onClick={() => navigate('/milestones/new')}
              className="mt-2 h-[44px] px-6 rounded-full bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-95 active:bg-[#993556] transition-all duration-150 touch-manipulation"
            >
              Add your first milestone
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {milestones.map((m) => (
              <MilestoneCard key={m.id} milestone={m} onClick={handleCardClick} />
            ))}
          </div>
        )}
      </main>

      <BottomNav />

      {/* Milestone Detail Sheet */}
      {selectedMilestone && (
        <MilestoneDetailSheet
          milestone={selectedMilestone}
          onEdit={(m) => {
            setSelectedMilestone(null)
            navigate(`/milestones/edit/${m.id}`)
          }}
          onDelete={handleDeleteClick}
          onClose={() => setSelectedMilestone(null)}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmSheet
        open={deleteTarget !== null}
        title="Delete milestone?"
        message="This memory will be gone forever. We can't recover it."
        confirmLabel="Delete"
        cancelLabel="Keep it"
        emoji="💔"
        loading={deleteMilestone.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
